/**
 * Invitation Acceptance Page
 * Handles team invitations: existing-user login or new-user account setup.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle, Users, Building2, Globe, Eye, EyeOff } from 'lucide-react';
import { teamApi, type InvitationInfo } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import vizzexLogo from '../../assets/VIZZEX.250.png';

type InviteStatus = 'loading' | 'ready' | 'setup' | 'accepting' | 'success' | 'error';

export default function InvitationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, user, refreshUser } = useAuth();

  const token = searchParams.get('token');

  const [status, setStatus] = useState<InviteStatus>('loading');
  const [error, setError] = useState('');
  const [invitation, setInvitation] = useState<InvitationInfo | null>(null);
  const [hasExistingAccount, setHasExistingAccount] = useState(false);
  const [emailMismatch, setEmailMismatch] = useState(false);

  // New-user setup form
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setError('No invitation token provided.');
      return;
    }

    const checkInvitation = async () => {
      try {
        setStatus('loading');
        const response = await teamApi.getInvitation(token);

        if (!response.success) {
          setStatus('error');
          setError('This invitation is invalid or has expired.');
          return;
        }

        if (response.requiresAuth) {
          setInvitation(response.invitation || null);
          setHasExistingAccount(!!response.hasExistingAccount);
          setEmailMismatch(!!response.emailMismatch);
          setStatus('ready');
        } else {
          // Logged in and auto-accepted
          setInvitation(response.invitation || null);
          setStatus('success');
          await refreshUser();
          setTimeout(() => navigate('/dashboard', { replace: true }), 2000);
        }
      } catch (e: unknown) {
        const err = e as { response?: { data?: { error?: string } }; message?: string };
        setStatus('error');
        setError(err.response?.data?.error || err.message || 'Invalid or expired invitation.');
      }
    };

    checkInvitation();
  }, [token, navigate, refreshUser]);

  // Logged-in user accepting (email already matches)
  const handleAccept = async () => {
    if (!token) return;
    setStatus('accepting');
    try {
      const response = await teamApi.getInvitation(token);
      if (response.success && !response.requiresAuth) {
        setStatus('success');
        await refreshUser();
        setTimeout(() => navigate('/dashboard', { replace: true }), 2000);
      } else {
        throw new Error('Failed to accept invitation');
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setStatus('error');
      setError(err.response?.data?.error || err.message || 'Failed to accept invitation.');
    }
  };

  // New user: set up account + accept
  const handleSetupAndAccept = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    if (password.length < 12) {
      setFormError('Password must be at least 12 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setFormError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      const response = await teamApi.acceptWithPassword(token!, name.trim() || invitation?.email?.split('@')[0] || '', password);
      if (response.success && response.token) {
        // Store token
        localStorage.setItem('auth_token', response.token);
        setStatus('success');
        await refreshUser();
        setTimeout(() => navigate('/dashboard', { replace: true }), 2000);
      } else {
        throw new Error('Failed to set up account');
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setFormError(err.response?.data?.error || err.message || 'Failed to set up account. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // -------------------------------------------------------------------------
  // No token
  // -------------------------------------------------------------------------
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="w-full max-w-md animate-slide-up">
          <div className="text-center mb-8">
            <img src={vizzexLogo} alt="VizzEx Pro" className="w-[250px] mx-auto mb-4" />
          </div>
          <div className="glass rounded-2xl p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-[var(--color-error)]/20 flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-[var(--color-error)]" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Invalid Invitation</h2>
            <p className="text-[var(--text-secondary)] mb-6">
              This invitation link is invalid. Please ask for a new invitation.
            </p>
            <Link to="/login" className="inline-block px-6 py-3 bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-600)] text-white font-medium rounded-lg hover:opacity-90 transition-opacity">
              Go to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Invitation detail card (shared between states)
  // -------------------------------------------------------------------------
  const InviteCard = () => invitation ? (
    <div className="p-4 bg-[var(--bg-tertiary)] rounded-lg mb-6 space-y-2">
      <div className="flex items-center gap-3">
        <Building2 className="w-4 h-4 text-[var(--color-primary-400)] flex-shrink-0" />
        <span className="font-semibold">{invitation.organizationName}</span>
      </div>
      {invitation.sites.length > 0 && (
        <div className="space-y-1">
          {invitation.sites.map(s => (
            <div key={s.id} className="flex items-center gap-3 pl-0.5">
              <Globe className="w-4 h-4 text-[var(--text-secondary)] flex-shrink-0" />
              <span className="text-sm text-white/80">{s.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  ) : null;

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-md animate-slide-up">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[var(--color-primary-400)] to-[var(--color-accent-500)] flex items-center justify-center mx-auto mb-4">
            <span className="text-white font-bold text-2xl">V</span>
          </div>
          <h1 className="text-2xl font-bold gradient-text">VizzEx Pro</h1>
        </div>

        <div className="glass rounded-2xl p-8">

          {/* Loading */}
          {status === 'loading' && (
            <div className="text-center">
              <Loader2 className="w-8 h-8 text-[var(--color-primary-400)] animate-spin mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">Checking invitation…</h2>
            </div>
          )}

          {/* Ready — show invite info and auth options */}
          {status === 'ready' && invitation && (
            <div>
              <div className="flex items-center justify-center gap-3 mb-4">
                <Users className="w-8 h-8 text-[var(--color-primary-400)]" />
              </div>
              <h2 className="text-xl font-semibold mb-1 text-center">You're invited!</h2>
              <p className="text-[var(--text-secondary)] text-sm text-center mb-5">
                You've been invited to access:
              </p>

              <InviteCard />

              {emailMismatch ? (
                <div>
                  <div className="p-4 bg-[var(--color-warning)]/10 border border-[var(--color-warning)]/30 rounded-lg mb-4">
                    <p className="text-sm text-[var(--color-warning)]">
                      This invitation was sent to <strong>{invitation.email}</strong>.<br />
                      You're logged in as <strong>{user?.email}</strong>.
                    </p>
                  </div>
                  <Link
                    to="/login"
                    className="block w-full py-3 px-4 bg-[var(--bg-tertiary)] text-white font-medium rounded-lg hover:bg-[var(--bg-tertiary)]/80 transition-colors text-center"
                  >
                    Log in with correct account
                  </Link>
                </div>
              ) : isAuthenticated && user?.email === invitation.email ? (
                <button
                  onClick={handleAccept}
                  className="w-full py-3 px-4 bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-600)] text-white font-medium rounded-lg hover:opacity-90 transition-opacity"
                >
                  Accept Invitation
                </button>
              ) : hasExistingAccount ? (
                <div className="space-y-3">
                  <p className="text-sm text-white/70 mb-2 text-center">Sign in to accept this invitation.</p>
                  <Link
                    to={`/login?redirect=/invite?token=${token}`}
                    className="block w-full py-3 px-4 bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-600)] text-white font-medium rounded-lg hover:opacity-90 transition-opacity text-center"
                  >
                    Sign in
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-white/70 mb-2 text-center">Set up your account to get started.</p>
                  <button
                    onClick={() => setStatus('setup')}
                    className="w-full py-3 px-4 bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-600)] text-white font-medium rounded-lg hover:opacity-90 transition-opacity"
                  >
                    Create Account &amp; Accept
                  </button>
                  <p className="text-center text-sm text-white/50">
                    Already have an account?{' '}
                    <Link to={`/login?redirect=/invite?token=${token}`} className="text-[var(--color-primary-400)] hover:underline">
                      Sign in
                    </Link>
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Setup — new user sets name + password */}
          {status === 'setup' && invitation && (
            <div>
              <h2 className="text-xl font-semibold mb-1 text-center">Create your account</h2>
              <p className="text-sm text-[var(--text-secondary)] text-center mb-5">
                You'll be joining <strong>{invitation.organizationName}</strong>.
              </p>

              <InviteCard />

              <form onSubmit={handleSetupAndAccept} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-1">Email</label>
                  <input
                    type="email"
                    value={invitation.email}
                    disabled
                    className="w-full px-3 py-2.5 bg-[var(--bg-tertiary)]/50 border border-white/10 rounded-lg text-white/50 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-1">Your name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="Jane Smith"
                    className="w-full px-3 py-2.5 bg-[var(--bg-tertiary)] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-[var(--color-primary-400)]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-1">Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="At least 8 characters"
                      required
                      className="w-full px-3 py-2.5 pr-10 bg-[var(--bg-tertiary)] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-[var(--color-primary-400)]"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-1">Confirm password</label>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    required
                    className="w-full px-3 py-2.5 bg-[var(--bg-tertiary)] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-[var(--color-primary-400)]"
                  />
                </div>

                {formError && (
                  <p className="text-sm text-[var(--color-error)]">{formError}</p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3 px-4 bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-600)] text-white font-medium rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {submitting ? 'Setting up…' : 'Create Account & Accept'}
                </button>

                <button type="button" onClick={() => setStatus('ready')} className="w-full text-sm text-white/40 hover:text-white/70 py-2">
                  Back
                </button>
              </form>
            </div>
          )}

          {/* Accepting */}
          {status === 'accepting' && (
            <div className="text-center">
              <Loader2 className="w-8 h-8 text-[var(--color-primary-400)] animate-spin mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">Accepting invitation…</h2>
            </div>
          )}

          {/* Success */}
          {status === 'success' && (
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[var(--color-success)]/20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-[var(--color-success)]" />
              </div>
              <h2 className="text-xl font-semibold mb-2">Welcome to the team!</h2>
              {invitation && (
                <p className="text-[var(--text-secondary)] text-sm">
                  You now have access to <strong>{invitation.organizationName}</strong>.
                </p>
              )}
              <p className="text-sm text-white/50 mt-4">Redirecting to dashboard…</p>
            </div>
          )}

          {/* Error */}
          {status === 'error' && (
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[var(--color-error)]/20 flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-[var(--color-error)]" />
              </div>
              <h2 className="text-xl font-semibold mb-2">Invitation Error</h2>
              <p className="text-[var(--text-secondary)] mb-6">{error}</p>
              <Link
                to="/dashboard"
                className="inline-block px-6 py-3 bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-600)] text-white font-medium rounded-lg hover:opacity-90 transition-opacity"
              >
                Go to Dashboard
              </Link>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
